import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import {
  Plus, FileText, Download, X, ChevronRight, ChevronDown,
  Pencil, Trash2, Check, ExternalLink, DollarSign, Calendar,
  Building2, ClipboardList,
} from "lucide-react";

// ── Constants ────────────────────────────────────────────────────────────────

const STAGES = [
  { id: "draft",           label: "Draft",           color: "bg-gray-100 text-gray-600",    dot: "bg-gray-400"    },
  { id: "sent_for_review", label: "Sent for Review", color: "bg-amber-50 text-amber-700",   dot: "bg-amber-400"   },
  { id: "approved",        label: "Approved",        color: "bg-blue-50 text-blue-700",     dot: "bg-blue-500"    },
  { id: "signed",          label: "Signed",          color: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
  { id: "archived",        label: "Archived",        color: "bg-slate-50 text-slate-500",   dot: "bg-slate-400"   },
];

const STAGE_MAP = Object.fromEntries(STAGES.map((s) => [s.id, s]));

const FMT_CURRENCY = (val, currency = "GBP") =>
  val == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(val);

const FMT_DATE = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

// ── Blank SOW form ───────────────────────────────────────────────────────────

const BLANK = {
  title: "", client_name: "", project_id: "", effective_date: "", expiry_date: "",
  payment_terms: "", currency: "GBP", total_value: "", notes: "",
  scope_items: [{ section: "", description: "", deliverable: "", timeline: "", price: "" }],
};

// ── PDF Print helper ─────────────────────────────────────────────────────────

const escHtml = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function printSOW(sow) {
  const stage = STAGE_MAP[sow.status] || STAGE_MAP.draft;
  const items = (sow.scope_items || []);
  const totalRows = items.map((it, i) => `
    <tr style="background:${i % 2 === 0 ? "#f9fafb" : "#fff"}">
      <td style="padding:10px 14px;border-bottom:1px solid #e5e7eb">${escHtml(it.section) || "—"}</td>
      <td style="padding:10px 14px;border-bottom:1px solid #e5e7eb">${escHtml(it.description)}</td>
      <td style="padding:10px 14px;border-bottom:1px solid #e5e7eb">${escHtml(it.deliverable) || "—"}</td>
      <td style="padding:10px 14px;border-bottom:1px solid #e5e7eb">${escHtml(it.timeline) || "—"}</td>
      <td style="padding:10px 14px;border-bottom:1px solid #e5e7eb;text-align:right">${it.price != null ? FMT_CURRENCY(it.price, sow.currency) : "—"}</td>
    </tr>`).join("");

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
  <title>SOW – ${escHtml(sow.title)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #111; background: #fff; font-size: 14px; }
    .page { max-width: 860px; margin: 0 auto; padding: 48px 48px 64px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 36px; }
    .brand { font-size: 22px; font-weight: 700; color: #7c3aed; letter-spacing: -0.5px; }
    .badge { display: inline-block; padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 600;
             background: #ede9fe; color: #6d28d9; }
    h1 { font-size: 26px; font-weight: 700; margin-bottom: 4px; }
    .sub { color: #6b7280; font-size: 14px; }
    .divider { border: none; border-top: 1px solid #e5e7eb; margin: 28px 0; }
    .meta-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-bottom: 32px; }
    .meta-item label { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: .08em; color: #9ca3af; margin-bottom: 3px; }
    .meta-item span { font-weight: 600; font-size: 14px; }
    h2 { font-size: 16px; font-weight: 700; margin-bottom: 14px; color: #374151; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    thead tr { background: #7c3aed; color: #fff; }
    thead th { padding: 10px 14px; text-align: left; font-weight: 600; }
    thead th:last-child { text-align: right; }
    .total-row td { padding: 12px 14px; font-weight: 700; font-size: 15px; border-top: 2px solid #7c3aed; }
    .total-row td:last-child { text-align: right; color: #7c3aed; }
    .notes { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-top: 28px; font-size: 13px; line-height: 1.6; white-space: pre-wrap; }
    .footer { margin-top: 48px; font-size: 12px; color: #9ca3af; text-align: center; border-top: 1px solid #e5e7eb; padding-top: 16px; }
    .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; margin-top: 40px; }
    .sig-box { border-top: 1px solid #d1d5db; padding-top: 8px; font-size: 12px; color: #6b7280; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
  </head><body><div class="page">
    <div class="header">
      <div>
        <div class="brand">CombinedWork</div>
        <div style="margin-top:4px;font-size:12px;color:#9ca3af">Statement of Work</div>
      </div>
      <div class="badge">${stage.label}</div>
    </div>
    <h1>${escHtml(sow.title)}</h1>
    <div class="sub">${escHtml(sow.client_name)}</div>
    <hr class="divider">
    <div class="meta-grid">
      <div class="meta-item"><label>Client</label><span>${escHtml(sow.client_name) || "—"}</span></div>
      <div class="meta-item"><label>Effective Date</label><span>${FMT_DATE(sow.effective_date)}</span></div>
      <div class="meta-item"><label>Expiry Date</label><span>${FMT_DATE(sow.expiry_date)}</span></div>
      <div class="meta-item"><label>Currency</label><span>${escHtml(sow.currency) || "GBP"}</span></div>
      <div class="meta-item"><label>Total Value</label><span>${FMT_CURRENCY(sow.total_value, sow.currency)}</span></div>
      <div class="meta-item"><label>Payment Terms</label><span>${escHtml(sow.payment_terms) || "—"}</span></div>
    </div>
    <hr class="divider">
    <h2>Scope of Work</h2>
    <table>
      <thead><tr>
        <th>Section</th><th>Description</th><th>Deliverable</th><th>Timeline</th><th>Price</th>
      </tr></thead>
      <tbody>${totalRows}</tbody>
      <tfoot><tr class="total-row">
        <td colspan="4">Total Value</td>
        <td>${FMT_CURRENCY(sow.total_value, sow.currency)}</td>
      </tr></tfoot>
    </table>
    ${sow.notes ? `<div class="notes"><strong>Notes</strong><br><br>${escHtml(sow.notes)}</div>` : ""}
    <div class="sig-grid">
      <div class="sig-box">Authorised Signatory (Client)<br><br><br>Name: ___________________________<br><br>Date: ____________________________</div>
      <div class="sig-box">Authorised Signatory (Vendor)<br><br><br>Name: ___________________________<br><br>Date: ____________________________</div>
    </div>
    <div class="footer">Generated by CombinedWork · ${new Date().toLocaleDateString()} · Document ID: ${escHtml(sow.sow_id)}</div>
  </div></body></html>`;

  const w = window.open("", "_blank");
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 400);
}

// ── SOW Form Modal ───────────────────────────────────────────────────────────

function SOWModal({ sow, projects, onClose, onSaved }) {
  const isEdit = Boolean(sow?.sow_id);
  const [form, setForm] = useState(
    isEdit
      ? {
          ...sow,
          total_value: sow.total_value ?? "",
          scope_items: (sow.scope_items || []).length > 0
            ? sow.scope_items.map((s) => ({ ...s, price: s.price ?? "" }))
            : [{ section: "", description: "", deliverable: "", timeline: "", price: "" }],
        }
      : { ...BLANK }
  );
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setItem = (i, k, v) =>
    setForm((f) => {
      const items = [...f.scope_items];
      items[i] = { ...items[i], [k]: v };
      return { ...f, scope_items: items };
    });

  const addItem = () =>
    setForm((f) => ({
      ...f,
      scope_items: [...f.scope_items, { section: "", description: "", deliverable: "", timeline: "", price: "" }],
    }));

  const removeItem = (i) =>
    setForm((f) => ({ ...f, scope_items: f.scope_items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        total_value: form.total_value !== "" ? parseFloat(form.total_value) : null,
        scope_items: form.scope_items
          .filter((s) => s.description.trim())
          .map((s) => ({ ...s, price: s.price !== "" ? parseFloat(s.price) : null })),
      };
      if (isEdit) {
        await api.put(`/sow/${sow.sow_id}`, payload);
      } else {
        await api.post("/sow", payload);
      }
      toast.success(isEdit ? "SOW updated" : "SOW created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="font-bold text-gray-900 text-lg">{isEdit ? "Edit SOW" : "New Statement of Work"}</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1">SOW Title *</label>
              <input
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                placeholder="e.g. Website Redesign Phase 1"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Client Name</label>
              <input
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                value={form.client_name}
                onChange={(e) => setField("client_name", e.target.value)}
                placeholder="Acme Corp"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Linked Project</label>
              <select
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                value={form.project_id || ""}
                onChange={(e) => setField("project_id", e.target.value || null)}
              >
                <option value="">— None —</option>
                {projects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Effective Date</label>
              <input
                type="date"
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                value={form.effective_date || ""}
                onChange={(e) => setField("effective_date", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Expiry Date</label>
              <input
                type="date"
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                value={form.expiry_date || ""}
                onChange={(e) => setField("expiry_date", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Currency</label>
              <select
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                value={form.currency}
                onChange={(e) => setField("currency", e.target.value)}
              >
                {["USD", "EUR", "GBP", "AUD", "CAD", "JPY", "INR"].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Total Value</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                value={form.total_value}
                onChange={(e) => setField("total_value", e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Terms</label>
              <input
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                value={form.payment_terms}
                onChange={(e) => setField("payment_terms", e.target.value)}
                placeholder="e.g. Net 30, 50% upfront"
              />
            </div>
          </div>

          {/* Scope Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 text-sm">Scope of Work Items</h3>
              <button
                onClick={addItem}
                className="inline-flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-semibold"
              >
                <Plus className="w-3.5 h-3.5" /> Add row
              </button>
            </div>
            <div className="space-y-3">
              {form.scope_items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <div className="col-span-2">
                    <label className="block text-[10px] text-gray-400 mb-0.5">Section</label>
                    <input
                      className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-violet-300"
                      value={it.section}
                      onChange={(e) => setItem(i, "section", e.target.value)}
                      placeholder="Design"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-[10px] text-gray-400 mb-0.5">Description *</label>
                    <input
                      className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-violet-300"
                      value={it.description}
                      onChange={(e) => setItem(i, "description", e.target.value)}
                      placeholder="Task description"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-[10px] text-gray-400 mb-0.5">Deliverable</label>
                    <input
                      className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-violet-300"
                      value={it.deliverable}
                      onChange={(e) => setItem(i, "deliverable", e.target.value)}
                      placeholder="Final mockups"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] text-gray-400 mb-0.5">Timeline</label>
                    <input
                      className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-violet-300"
                      value={it.timeline}
                      onChange={(e) => setItem(i, "timeline", e.target.value)}
                      placeholder="2 weeks"
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-[10px] text-gray-400 mb-0.5">Price</label>
                    <input
                      type="number"
                      min="0"
                      className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-violet-300"
                      value={it.price}
                      onChange={(e) => setItem(i, "price", e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="col-span-1 flex items-end pb-1">
                    <button
                      onClick={() => removeItem(i)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Notes / Additional Terms</label>
            <textarea
              rows={3}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-300"
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Any additional terms, conditions, or notes…"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-medium text-gray-600 border border-gray-200 hover:border-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2 rounded-full text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create SOW"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SOW Detail Drawer ────────────────────────────────────────────────────────

function SOWDrawer({ sow, projects, onClose, onEdit, onDelete, onStatusChange }) {
  const [changingStatus, setChangingStatus] = useState(false);
  const stage = STAGE_MAP[sow.status] || STAGE_MAP.draft;
  const projectName = projects.find((p) => p.project_id === sow.project_id)?.name;

  const moveStatus = async (newStatus) => {
    setChangingStatus(true);
    try {
      await onStatusChange(sow.sow_id, newStatus);
    } finally {
      setChangingStatus(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col h-full">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-0.5 mb-2 ${stage.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${stage.dot}`} />
                {stage.label}
              </span>
              <h2 className="text-lg font-bold text-gray-900 leading-tight">{sow.title}</h2>
              {sow.client_name && (
                <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                  <Building2 className="w-3.5 h-3.5" />
                  {sow.client_name}
                </div>
              )}
            </div>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 shrink-0 transition-colors">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Pipeline status strip */}
          <div className="flex items-center gap-1 mt-4 overflow-x-auto pb-1">
            {STAGES.map((s, idx) => {
              const active = s.id === sow.status;
              const passed = STAGES.findIndex((x) => x.id === sow.status) > idx;
              return (
                <button
                  key={s.id}
                  disabled={changingStatus}
                  onClick={() => moveStatus(s.id)}
                  className={`flex-1 min-w-0 flex items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold transition-all border ${
                    active
                      ? "bg-violet-600 text-white border-violet-600 shadow"
                      : passed
                      ? "bg-violet-50 text-violet-500 border-violet-100"
                      : "bg-gray-50 text-gray-400 border-gray-100 hover:border-gray-200"
                  }`}
                >
                  {passed && !active && <Check className="w-2.5 h-2.5 shrink-0" />}
                  <span className="truncate">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Meta */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "Effective Date", value: FMT_DATE(sow.effective_date) },
              { label: "Expiry Date", value: FMT_DATE(sow.expiry_date) },
              { label: "Total Value", value: FMT_CURRENCY(sow.total_value, sow.currency) },
              { label: "Payment Terms", value: sow.payment_terms || "—" },
              { label: "Currency", value: sow.currency || "GBP" },
              { label: "Linked Project", value: projectName || "—" },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-0.5">{label}</p>
                <p className="text-sm font-semibold text-gray-800">{value}</p>
              </div>
            ))}
          </div>

          {/* Scope Items */}
          {(sow.scope_items || []).length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Scope of Work</h3>
              <div className="rounded-xl border border-gray-100 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Description</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Timeline</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-500">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sow.scope_items.map((it, i) => (
                      <tr key={i} className="border-b border-gray-50 last:border-0">
                        <td className="px-3 py-2">
                          {it.section && <span className="text-violet-600 font-semibold mr-1">{it.section}:</span>}
                          {it.description}
                          {it.deliverable && <div className="text-gray-400 text-[10px] mt-0.5">↳ {it.deliverable}</div>}
                        </td>
                        <td className="px-3 py-2 text-gray-500">{it.timeline || "—"}</td>
                        <td className="px-3 py-2 text-right font-semibold text-gray-700">
                          {it.price != null ? FMT_CURRENCY(it.price, sow.currency) : "—"}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-violet-50">
                      <td colSpan={2} className="px-3 py-2 font-bold text-gray-700">Total</td>
                      <td className="px-3 py-2 text-right font-bold text-violet-700">
                        {FMT_CURRENCY(sow.total_value, sow.currency)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes */}
          {sow.notes && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Notes</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{sow.notes}</p>
            </div>
          )}

          {/* Created */}
          <div className="text-[11px] text-gray-400">
            Created by {sow.created_by_name} · {FMT_DATE(sow.created_at)}
          </div>
        </div>

        {/* Actions */}
        <div className="shrink-0 px-6 py-4 border-t border-gray-100 flex items-center gap-2">
          <button
            onClick={() => printSOW(sow)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:border-violet-300 hover:text-violet-700 transition-colors"
          >
            <Download className="w-4 h-4" /> Download PDF
          </button>
          <button
            onClick={() => onEdit(sow)}
            className="p-2.5 rounded-full border border-gray-200 text-gray-600 hover:border-violet-300 hover:text-violet-700 transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(sow.sow_id)}
            className="p-2.5 rounded-full border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SOW Card ─────────────────────────────────────────────────────────────────

function SOWCard({ sow, projects, onClick }) {
  const stage = STAGE_MAP[sow.status] || STAGE_MAP.draft;
  const projectName = projects.find((p) => p.project_id === sow.project_id)?.name;

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl border border-gray-100 p-4 cursor-pointer hover:shadow-md hover:border-violet-100 transition-all group"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-violet-50 grid place-items-center shrink-0">
          <FileText className="w-4.5 h-4.5 text-violet-500" />
        </div>
        <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${stage.color}`}>{stage.label}</span>
      </div>
      <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-1 group-hover:text-violet-700 transition-colors line-clamp-2">
        {sow.title}
      </h3>
      {sow.client_name && (
        <p className="text-[11px] text-gray-400 flex items-center gap-1 mb-2">
          <Building2 className="w-3 h-3" /> {sow.client_name}
        </p>
      )}
      {projectName && (
        <p className="text-[11px] text-violet-500 font-medium mb-2 truncate">{projectName}</p>
      )}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
        <span className="text-xs font-bold text-gray-700">
          {FMT_CURRENCY(sow.total_value, sow.currency)}
        </span>
        {sow.effective_date && (
          <span className="text-[10px] text-gray-400">{FMT_DATE(sow.effective_date)}</span>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const SOWPipeline = () => {
  const { user } = useAuth();
  const [sows, setSows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { sow } | { sow: null }
  const [drawer, setDrawer] = useState(null); // selected SOW

  const load = useCallback(async () => {
    try {
      const [sowRes, projRes] = await Promise.all([
        api.get("/sow"),
        api.get("/projects"),
      ]);
      setSows(sowRes.data || []);
      setProjects(projRes.data || []);
    } catch (e) {
      toast.error("Failed to load SOWs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = () => { setModal(null); setDrawer(null); load(); };

  const handleStatusChange = async (sowId, newStatus) => {
    try {
      const updated = await api.post(`/sow/${sowId}/status`, { status: newStatus });
      setSows((prev) => prev.map((s) => (s.sow_id === sowId ? updated.data : s)));
      if (drawer?.sow_id === sowId) setDrawer(updated.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed");
    }
  };

  const handleDelete = async (sowId) => {
    if (!window.confirm("Delete this SOW? This cannot be undone.")) return;
    try {
      await api.delete(`/sow/${sowId}`);
      toast.success("SOW deleted");
      setDrawer(null);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const totalValue = sows.reduce((sum, s) => sum + (s.total_value || 0), 0);
  const signedValue = sows.filter((s) => s.status === "signed").reduce((sum, s) => sum + (s.total_value || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-violet-500 mb-0.5">
              Workflow
            </p>
            <h1 className="text-2xl font-bold text-gray-900">SOW Pipeline</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Manage Statements of Work across all stages — from draft to signed.
            </p>
          </div>
          <button
            onClick={() => setModal({ sow: null })}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold shadow-md shadow-violet-200 transition-colors"
            data-testid="sow-new-btn"
          >
            <Plus className="w-4 h-4" /> New SOW
          </button>
        </div>

        {/* Summary bar */}
        <div className="grid grid-cols-4 gap-4 mt-5">
          {[
            { label: "Total SOWs", value: sows.length, icon: ClipboardList, color: "bg-violet-50 text-violet-600" },
            { label: "Pipeline Value", value: FMT_CURRENCY(totalValue), icon: DollarSign, color: "bg-blue-50 text-blue-600" },
            { label: "Signed Value", value: FMT_CURRENCY(signedValue), icon: Check, color: "bg-emerald-50 text-emerald-600" },
            { label: "Awaiting Signature", value: sows.filter((s) => s.status === "approved").length, icon: Calendar, color: "bg-amber-50 text-amber-600" },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="flex items-center gap-3 bg-white rounded-2xl border border-gray-100 px-4 py-3">
              <div className={`w-9 h-9 rounded-xl ${color} grid place-items-center shrink-0`}>
                <Icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-gray-400">{label}</p>
                <p className="font-bold text-gray-900 text-sm">{value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Kanban columns */}
      <div className="px-6 py-6 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-sm">Loading SOWs…</div>
        ) : (
          <div className="flex gap-4 min-w-max">
            {STAGES.map((stage) => {
              const colSows = sows.filter((s) => s.status === stage.id);
              const colValue = colSows.reduce((sum, s) => sum + (s.total_value || 0), 0);
              return (
                <div key={stage.id} className="w-72 flex flex-col gap-3" data-testid={`sow-col-${stage.id}`}>
                  {/* Column header */}
                  <div className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${stage.dot}`} />
                      <span className="text-sm font-semibold text-gray-700">{stage.label}</span>
                      <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5 font-mono">
                        {colSows.length}
                      </span>
                    </div>
                    {colValue > 0 && (
                      <span className="text-xs text-gray-400 font-medium">{FMT_CURRENCY(colValue)}</span>
                    )}
                  </div>

                  {/* Cards */}
                  <div className="space-y-3 min-h-[80px]">
                    {colSows.length === 0 ? (
                      <div className="border-2 border-dashed border-gray-200 rounded-2xl h-20 flex items-center justify-center text-xs text-gray-300">
                        No SOWs
                      </div>
                    ) : (
                      colSows.map((sow) => (
                        <SOWCard
                          key={sow.sow_id}
                          sow={sow}
                          projects={projects}
                          onClick={() => setDrawer(sow)}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals & Drawers */}
      {modal !== null && (
        <SOWModal
          sow={modal.sow}
          projects={projects}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
      {drawer && (
        <SOWDrawer
          sow={drawer}
          projects={projects}
          onClose={() => setDrawer(null)}
          onEdit={(s) => { setDrawer(null); setModal({ sow: s }); }}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
};

export default SOWPipeline;
